import {AfterViewInit, Component, computed, effect, inject, input, signal, ViewChild} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CheckService } from '../../../core/checks/check';
import { Check } from '../../../core/models/checks-model';
import { RouterLink } from '@angular/router';
import { MatIcon } from "@angular/material/icon";
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { User } from '../../../core/models/user-model';
import { Avatar } from '../../../core/models/avatar-model';
import { Project } from '../../../core/models/project-model';
import { DatePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

interface CheckVM extends Omit<Check, 'creator' | 'checker12' | 'checker2'> {
  creator:  User | null;
  checker12: User | null;
  checker2:  User | null;
}

@Component({
  selector: 'app-all-checks',
  imports: [MatTableModule, RouterLink, MatIcon, MatSortModule, MatInputModule, MatProgressBarModule, MatTooltipModule],
  providers: [DatePipe],
  templateUrl: './all-checks.html',
  styleUrl: './all-checks.scss',
})
export class AllChecks implements AfterViewInit {
    allChecks = new MatTableDataSource<CheckVM>();

    readonly checkService = inject(CheckService);
    private readonly datePipe = inject(DatePipe);

    // Optional – leer wenn als Standalone-Route verwendet
    readonly usersById = input<Map<string, Avatar>>(new Map());
    readonly projectsById = input<Map<string, Project>>(new Map());
    readonly projectCode = input<string | null>(null);
    readonly newCheckId = input<string | null>(null);

    // Internes Signal – kann bei Sort-Klick lokal zurückgesetzt werden
    readonly pinnedNewCheckId = signal<string | null>(null);

    constructor() {
      effect(() => {
        this.pinnedNewCheckId.set(this.newCheckId());
      });
    }

    readonly checks = this.checkService.createChecksSignal(
      () => this.usersById(),
      () => this.projectsById(),
      () => this.projectCode()
    );

    /** true solange der HTTP-Request läuft (Signal liefert null) */
    readonly isLoading = computed(() => this.projectCode() !== null && this.checks() === null);

    readonly filteredChecks = computed(() => {
      const code = this.projectCode();
      const list = this.checks() ?? [];
      if (!code) return list;
      return list.filter(c => c.project?.contractNr === code);
    });

    applyFilter(event: Event) {
      const filterValue = (event.target as HTMLInputElement).value;
      this.allChecks.filter = filterValue.trim().toLowerCase();
      if (this.allChecks.paginator) {
        this.allChecks.paginator.firstPage();
      }
    }

    checkDataViewModels = computed(() => {
      const usersById = this.usersById();
      const pinned = this.pinnedNewCheckId();
      let checks = this.filteredChecks()
        .map(check => ({
          ...check,
          creator:   check.creator?.oid   ? (usersById.get(check.creator.oid)   ?? check.creator)   : null,
          checker12: check.checker12?.oid ? (usersById.get(check.checker12.oid) ?? check.checker12) : null,
          checker2:  check.checker2?.oid  ? (usersById.get(check.checker2.oid)  ?? check.checker2)  : null,
        }));
      if (pinned) {
        const idx = checks.findIndex(c => c.id === pinned);
        if (idx > -1) {
          const newCheck = checks.splice(idx, 1)[0];
          checks = [newCheck, ...checks];
        }
      }
      return checks;
    });

    clearPin(): void {
      this.pinnedNewCheckId.set(null);
    }

    allChecksData = effect(() => {
      this.allChecks.data = this.checkDataViewModels();
    });


    formatDate(date: Date | null | undefined): string {
      return this.datePipe.transform(date, 'dd.MM.yyyy') ?? '';
    }

    displayedColumns: string[] = [
      'displayName',
      'creator',
      'checker12',
      'dueDate1',
      'checker2',
      'dueDate2',
    ];

    @ViewChild(MatSort) sort!: MatSort;
    ngAfterViewInit() {
      this.allChecks.sort = this.sort;
      this.allChecks.filterPredicate = (check, filter) => {
        const term = filter.toLowerCase();
        const creator   = check.creator?.displayName?.toLowerCase()  ?? '';
        const checker12 = check.checker12?.displayName?.toLowerCase() ?? '';
        const checker2  = check.checker2?.displayName?.toLowerCase()  ?? '';
        return (
          check.displayName?.toLowerCase().includes(term) ||
          creator.includes(term)   ||
          checker12.includes(term) ||
          checker2.includes(term)
        );
      };
      this.allChecks.sortingDataAccessor = (row, column) => {
        switch (column) {
          case 'creator':   return row.creator?.displayName?.toLowerCase()  ?? '';
          case 'checker12': return row.checker12?.displayName?.toLowerCase() ?? '';
          case 'checker2':  return row.checker2?.displayName?.toLowerCase()  ?? '';
          default:          return (row as any)[column];
        }
      };
    }
}
